import React, { useEffect, useRef, useState } from "react";
import Accordion from "../../Accordian/Accordian";
import Advanced from "../mp4FileUpload/Advanced";
import style from "../Information/style.module.css";
import { MdOutlineSubtitles } from "react-icons/md";
import { IoAdd, IoClose, IoChevronDown, IoChevronUp } from "react-icons/io5";
import MediaLibraryModal from "../mediaLibrary/MediaLibraryModal";
import Button from "../button/Button";
import Hls from "hls.js";
import { HiSparkles } from "react-icons/hi2";
import { getTrailerType } from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";
import useAiTrailer from "../../hooks/useAiTrailer";
import { useAiEnabled } from "../../context/AppConfigContext";
import { detectSubtitleLanguage, subtitleLanguageName } from "../../utils/subtitleLanguage";

const HLSPlayer = ({ src, className }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play();
      });
      return () => hls.destroy();
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      video.play();
    }
  }, [src]);

  return <video ref={videoRef} className={className} controls style={{ padding: 0, cursor: "default" }} />;
};

const defaultTrailer = () => ({
  id: Date.now(),
  label: "",
  type: "",
  description: "",
  url: "",
  duration: null,
  videoFile: null,
  thumbnail: null,
  subtitlesEnabled: false,
  subtitleFiles: [null, null, null],
  expanded: true,
  saved: false,
});

const TrailerClips = ({
  movieData,
  handleCastCrewChange,
  selectedVideos,
  setSelectedVideos,
  setTrailer,
  setVideo,
  trailersList,
  setTrailersList,
  soloProps,
  contentTitle,
  sourceVideo,
}) => {
  const subtitleRefs = useRef({});

  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [activeTrailerId, setActiveTrailerId] = useState(null);
  const [activeAssetType, setActiveAssetType] = useState(null);
  const [trailerTypes, setTrailerTypes] = useState([]);

  // ── AI trailer generation ──────────────────────────────────────────────
  const aiTrailer = useAiTrailer();
  // AI master switch, from GET /config (CMS Settings page).
  const aiEnabled = useAiEnabled();
  const [trailerLength, setTrailerLength] = useState(30);
  // Ensures a completed trailer is applied to the list only once per result.
  const aiTrailerAppliedRef = useRef(null);

  // Show the AI bar whenever a main video is selected. If a trailer already
  // exists (e.g. the placeholder the metadata AI drops in), generating replaces it.
  const canGenerate = aiEnabled && Boolean(sourceVideo);
  const hasExistingTrailer = trailersList.length >= 1;

  const handleGenerateTrailer = () => {
    if (!sourceVideo) {
      showToast("Select or upload the main video first.", "error");
      return;
    }
    aiTrailerAppliedRef.current = null;
    aiTrailer.generate(sourceVideo, {
      durationTarget: trailerLength,
      title: contentTitle || undefined,
    });
  };

  // When a trailer finishes rendering, drop it into the list as a prefilled,
  // AI-generated trailer card (mirrors how metadata auto-fill works upstream).
  useEffect(() => {
    if (aiTrailer.status !== "complete" || !aiTrailer.result) return;
    if (aiTrailerAppliedRef.current === aiTrailer.result) return;
    aiTrailerAppliedRef.current = aiTrailer.result;

    const t = aiTrailer.result;
    (t.warnings || []).forEach((w) => showToast(w, "warning"));

    // The rendered AI trailer replaces whatever was in the list (including the
    // placeholder trailer the metadata AI prefills). We keep the existing card's
    // label/type if it had a real one, so a user's edits aren't lost.
    setTrailersList((prev) => {
      const existing = prev[0];
      if (existing) {
        showToast("Replaced the existing trailer with the AI-generated one.", "success");
      }
      return [
        {
          id: existing?.id ?? Date.now(),
          label: existing?.label || t.title || `${contentTitle || "AI"} — Official Trailer`,
          type: existing?.type || "",
          description: existing?.description || t.tagline || "",
          url: "",
          duration: t.duration ?? null,
          // videoFile carries the rendered MP4 so the card previews + saves it.
          videoFile: { title: t.title || "AI Trailer", bunny_cdn_url: t.url, url: t.url },
          thumbnail: existing?.thumbnail || null,
          aiGenerated: true,
          subtitlesEnabled: existing?.subtitlesEnabled || false,
          subtitleFiles: existing?.subtitleFiles || [null, null, null],
          expanded: true,
          saved: false,
        },
      ];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiTrailer.status, aiTrailer.result, contentTitle]);

  useEffect(() => {
    getTrailerType()
      .then((res) => {
        const types = res?.data?.data?.map((item) => ({
          key: item.key,
          value: item.value,
        })) || [];
        setTrailerTypes(types);
      })
      .catch((err) => console.error("Failed to load trailer types", err));
  }, []);

  const getSubtitleRef = (id, idx) => {
    const key = `${id}_${idx}`;
    if (!subtitleRefs.current[key]) subtitleRefs.current[key] = React.createRef();
    return subtitleRefs.current[key];
  };

  const openMediaModal = (trailerId, assetType) => {
    setActiveTrailerId(trailerId);
    setActiveAssetType(assetType);
    setMediaModalOpen(true);
  };

  const addTrailer = () => {
    setTrailersList((prev) => (prev.length >= 1 ? prev : [...prev, defaultTrailer()]));
  };

  useEffect(() => {
    if (!movieData?.trailer) return;

    const apiTrailer = movieData.trailer;
    if (!apiTrailer.play_link && !apiTrailer.id && !apiTrailer.thumbnail) return;

    // Build prefilled subtitleFiles from trailer_subtitles array
    const subs = Array.isArray(apiTrailer.trailer_subtitles) && apiTrailer.trailer_subtitles.length > 0
      ? apiTrailer.trailer_subtitles
      : [];

    const subtitleFiles = [null, null, null];
    subs.forEach((sub, idx) => {
      if (idx < 3) {
        subtitleFiles[idx] = {
          name: `${sub.language}.${sub.format}`,
          url: sub.subtitle_url,
          language: sub.language,
          format: sub.format,
          id: sub.id,
          isExisting: true,
        };
      }
    });

    setTrailersList([
      {
        id: apiTrailer.id || Date.now(),
        label: apiTrailer.title || "",
        type: apiTrailer.trailer_type || "",
        description: apiTrailer.description || "",
        url: apiTrailer.external_url || "",
        duration: apiTrailer.duration || null,
        videoFile: {
          title: apiTrailer.title || "Trailer",
          HLSPlaylistURL: apiTrailer.play_link,
        },
        thumbnail: apiTrailer.thumbnail || null,
        subtitlesEnabled: subs.length > 0,
        subtitleFiles,
        expanded: false,
        saved: true,
      },
    ]);
  }, [movieData]);

  const removeTrailer = (id) => {
    setTrailersList((prev) => prev.filter((t) => t.id !== id));
  };

  const saveTrailer = (id) => {
    const current = trailersList?.find((t) => t.id === id);
    if (current) {
      const hasLabel = typeof current.label === "string" && current.label.trim() !== "";
      const hasVideo = Boolean(
        current.videoFile?.id ||
          current.videoFile?.HLSPlaylistURL ||
          current.videoFile?.play_link ||
          current.videoFile?.bunny_cdn_url ||
          (typeof current.url === "string" && current.url.trim() !== "")
      );
      if (hasLabel && !hasVideo) {
        showToast("Please select a video file for the trailer.", "error");
        return;
      }
      if (!hasLabel && hasVideo) {
        showToast("Please enter a trailer title.", "error");
        return;
      }
    }
    setTrailersList((prev) =>
      prev?.map((t) =>
        t.id === id ? { ...t, expanded: false, saved: true } : t
      )
    );
  };

  const toggleExpand = (id) => {
    setTrailersList((prev) =>
      prev?.map((t) =>
        t.id === id ? { ...t, expanded: !t.expanded } : t
      )
    );
  };

  const updateTrailer = (id, field, value) => {
    setTrailersList((prev) =>
      prev?.map((t) => (t.id === id ? { ...t, [field]: value } : t))
    );
  };

  const handleVideoSelect = (item) => {
    if (!item || !activeTrailerId) return;

    setTrailersList((prev) => {
      const updated = prev.map((t) =>
        t.id === activeTrailerId
          ? {
            ...t,
            videoFile: item,
            duration: item?.length ?? t.duration,
          }
          : t
      );

      if (setTrailer) {
        const updatedTrailer = updated.find((t) => t.id === activeTrailerId);
        setTrailer(updatedTrailer);
      }

      return updated;
    });
  };

  const handleImageSelect = (item) => {
    if (!item || !activeTrailerId) return;
    const thumbUrl = item?.bunny_cdn_url || item?.url || "";
    updateTrailer(activeTrailerId, "thumbnail", thumbUrl);
  };

  const handleSubtitleFile = async (id, idx, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate: only allow .srt files
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith(".srt")) {
      showToast("Only .srt subtitle files are allowed. Please select a valid .srt file.");
      e.target.value = "";
      return;
    }

    // Label the file by the language in its name (e.g. "trailer.es.srt",
    // "Japanese.srt"; falls back to its text) instead of its slot position.
    // Carried on the File itself, like AI-generated subtitles, so the save
    // payload (Mp4FileUpload) picks it up.
    file.language = await detectSubtitleLanguage(file);

    setTrailersList((prev) =>
      prev?.map((t) => {
        if (t.id !== id) return t;
        const updated = [...t.subtitleFiles];
        // Never save two of this trailer's subtitles under one language — on a
        // clash fall back to the old slot defaults (en, then hi), else "other".
        const taken = new Set(
          updated
            .filter((f, i) => i !== idx && f?.language)
            .map((f) => String(f.language).toLowerCase())
        );
        let language = file.language ? String(file.language).toLowerCase() : null;
        if (language && taken.has(language)) {
          language = ["en", "hi"].find((l) => !taken.has(l)) || "other";
        }
        if (language) file.language = language;
        updated[idx] = file; // real File object, no isExisting flag
        return { ...t, subtitleFiles: updated };
      })
    );
  };

  const removeSubtitleFile = (id, idx) => {
    setTrailersList((prev) =>
      prev?.map((t) => {
        if (t.id !== id) return t;
        const updated = [...t.subtitleFiles];
        updated[idx] = null;
        const key = `${id}_${idx}`;
        if (subtitleRefs.current[key]?.current) {
          subtitleRefs.current[key].current.value = "";
        }
        return { ...t, subtitleFiles: updated };
      })
    );
  };

  return (
    <>
      <Accordion
        title="Trailers & Clips"
        icon={<img src={`${process.env.PUBLIC_URL}/castMembers.png`} alt="" width={18} height={18} />}
        openId="cast"
        {...soloProps}
      >
        <p className={style.trailerDescription}>
          Manage all trailers, teasers, clips, and bonus content for{" "}
          <span className={style.trailerDescriptionHighlight}>
            {contentTitle || "this content"}
          </span>
          . Each trailer can have its own video file, thumbnail, subtitles, and publish settings.
        </p>

        <div className={style.trailerOuterBox}>

          <div className={style.trailerBoxHeader}>
            <div>
              <p className={style.trailerBoxTitle}>Trailers &amp; Clips</p>
              <p className={style.trailerBoxMeta}>
                {trailersList.length} / 1 · Official trailer, teaser, clip, or BTS
              </p>
            </div>
            {trailersList.length < 1 && (
              <Button onClick={addTrailer} className={style.addTrailerBtn} variant="dark">
                + Add Trailer
              </Button>
            )}
          </div>

          {/* ── AI trailer generator ── */}
          {aiEnabled && (canGenerate || aiTrailer.isRunning || aiTrailer.isFailed) && (
            <div className={style.aiTrailerBar}>
              <div className={style.aiTrailerBarText}>
                <p className={style.aiTrailerBarTitle}>
                  <HiSparkles /> Generate trailer with AI
                </p>
                <p className={style.aiTrailerBarSub}>
                  {aiTrailer.isRunning
                    ? "Directing and rendering a movie-style trailer from your video…"
                    : hasExistingTrailer
                    ? "Build a movie-style trailer from your video — it will replace the trailer below. Uses the video's own audio."
                    : "AI picks the best moments, adds title cards, and builds a trailer from your uploaded video — using the video's own audio."}
                </p>

                {aiTrailer.isRunning && (
                  <div className={style.aiTrailerProgress}>
                    <div className={style.aiTrailerProgressLabel}>
                      <span>
                        {aiTrailer.activeStage === "planning_trailer"
                          ? "Directing the trailer…"
                          : aiTrailer.activeStage === "rendering_trailer"
                          ? "Rendering (cutting, crossfades, audio)…"
                          : "Uploading…"}
                      </span>
                      <span>{aiTrailer.progress}%</span>
                    </div>
                    <div className={style.aiTrailerProgressTrack}>
                      <div
                        className={style.aiTrailerProgressFill}
                        style={{ width: `${aiTrailer.progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {aiTrailer.isFailed && (
                  <p className={style.aiTrailerError}>
                    {aiTrailer.error || "Trailer generation failed."}
                  </p>
                )}
              </div>

              <div className={style.aiTrailerControls}>
                <select
                  className={style.aiTrailerLengthSelect}
                  value={trailerLength}
                  disabled={aiTrailer.isRunning}
                  onChange={(e) => setTrailerLength(Number(e.target.value))}
                  aria-label="Trailer length"
                >
                  <option value={20}>20s</option>
                  <option value={30}>30s</option>
                  <option value={45}>45s</option>
                  <option value={60}>60s</option>
                </select>
                <Button
                  variant="dark"
                  className={style.addTrailerBtn}
                  onClick={handleGenerateTrailer}
                  loading={aiTrailer.isRunning}
                  disabled={aiTrailer.isRunning || !sourceVideo}
                  iconLeft={<HiSparkles />}
                >
                  {aiTrailer.isFailed
                    ? "Retry"
                    : hasExistingTrailer
                    ? "Replace with AI Trailer"
                    : "Generate AI Trailer"}
                </Button>
              </div>
            </div>
          )}

          {trailersList.length === 0 && (
            <p className={style.trailerEmptyText}>
              No trailers added yet – click <strong>Add Trailer</strong>{canGenerate ? " or Generate AI Trailer" : ""} to get started
            </p>
          )}

          {trailersList?.map((trailer, index) => (
            <div key={trailer.id} className={style.trailerCard}>

              <div className={style.trailerCardHeader}>
                <div className={style.trailerCardIndex}>{index + 1}</div>
                <div className={style.trailerCardMeta}>
                  <p className={style.trailerCardTitle}>
                    {trailer.label || "Untitled Trailer"}
                    {aiEnabled && trailer.aiGenerated && (
                      <span className={style.aiGenBadge}>
                        <HiSparkles /> AI
                      </span>
                    )}
                  </p>
                </div>
                <Button
                  variant="dark"
                  className={style.trailerRemoveBtn}
                  onClick={() => toggleExpand(trailer.id)}
                  aria-label="Toggle expand"
                  iconLeft={trailer.expanded ? <IoChevronUp /> : <IoChevronDown />}
                />
                <Button
                  variant="dark"
                  className={style.trailerRemoveBtn}
                  onClick={() => removeTrailer(trailer.id)}
                  aria-label="Remove trailer"
                  iconLeft={<IoClose />}
                />
              </div>

              {trailer.expanded && (
                <div className={style.trailerCardBody}>

                  <div className={style.trailerFormRow}>
                    <div className={style.trailerFormGroup}>
                      <label className={style.trailerFormLabel}>
                        Trailer Label <span style={{ color: "var(--app-primary)" }}>*</span>
                      </label>
                      <input
                        className={style.trailerFormInput}
                        placeholder="e.g. Official Trailer"
                        maxLength={80}
                        value={trailer.label}
                        onChange={(e) => updateTrailer(trailer.id, "label", e.target.value)}
                      />
                    </div>
                    <div className={style.trailerFormGroup}>
                      <label className={style.trailerFormLabel}>Trailer Type</label>
                      <select
                        className={style.trailerFormInput}
                        value={trailer.type}
                        onChange={(e) => updateTrailer(trailer.id, "type", e.target.value)}
                      >
                        <option value="">Select type</option>
                        {trailerTypes?.map((t) => (
                          <option key={t.key} value={t.key}>{t.value}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className={style.trailerFormRow}>
                    <div className={style.trailerFormGroup}>
                      <label className={style.trailerFormLabel}>Youtube / External URL</label>
                      <input
                        className={style.trailerFormInput}
                        placeholder="URL"
                        maxLength={100}
                        value={trailer.url}
                        onChange={(e) => updateTrailer(trailer.id, "url", e.target.value)}
                      />
                    </div>
                    <div className={style.trailerFormGroup}>
                      <label className={style.trailerFormLabel}>
                        Duration <span className={style.trailerFormLabelLight}>(in seconds)</span>
                      </label>
                      <input
                        type="number"
                        className={style.trailerFormInput}
                        placeholder="120 seconds"
                        min="0"
                        value={trailer.duration}
                        onChange={(e) => updateTrailer(trailer.id, "duration", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className={style.trailerFormRow}>
                    <div className={style.assetCellWrapper}>
                      {trailer.videoFile?.HLSPlaylistURL ? (
                        <HLSPlayer
                          key={trailer.videoFile.HLSPlaylistURL}
                          src={trailer.videoFile.HLSPlaylistURL}
                          className={style.mediaAssetCell}
                        />
                      ) : trailer.videoFile?.bunny_cdn_url || trailer.videoFile?.url ? (
                        // Plain MP4 (e.g. the AI-rendered trailer) — native <video>.
                        <video
                          key={trailer.videoFile.bunny_cdn_url || trailer.videoFile.url}
                          src={trailer.videoFile.bunny_cdn_url || trailer.videoFile.url}
                          className={style.mediaAssetCell}
                          controls
                          style={{ padding: 0, cursor: "default" }}
                        />
                      ) : (
                        <button
                          type="button"
                          className={style.mediaAssetCell}
                          onClick={() => openMediaModal(trailer.id, "video")}
                        >
                          <p className={style.mediaAssetTitle}>Video File</p>
                          <p className={style.mediaAssetSub}>MP4 · H.264/H.265 master</p>
                        </button>
                      )}
                      {trailer.videoFile && (
                        <button
                          type="button"
                          className={style.assetRemoveBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            updateTrailer(trailer.id, "videoFile", null);
                            updateTrailer(trailer.id, "url", "");
                          }}
                        >
                          <IoClose />
                        </button>
                      )}
                    </div>
                    <div className={style.assetCellWrapper}>
                      <button
                        type="button"
                        className={style.mediaAssetCell}
                        onClick={() => openMediaModal(trailer.id, "thumbnail")}
                      >
                        {trailer.thumbnail ? (
                          <img
                            src={trailer.thumbnail}
                            alt="thumb"
                            style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "6px" }}
                          />
                        ) : (
                          <>
                            <p className={style.mediaAssetTitle}>Thumbnail</p>
                            <p className={style.mediaAssetSub}>landscape · 16:9 · JPG/PNG</p>
                          </>
                        )}
                      </button>
                      {trailer.thumbnail && (
                        <button
                          type="button"
                          className={style.assetRemoveBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            updateTrailer(trailer.id, "thumbnail", null);
                          }}
                        >
                          <IoClose />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className={style.mediaToggleRow}>
                    <span className={style.mediaToggleLabel}>Subtitles &amp; Captions</span>
                    <label className={style.mediaToggleTrack}>
                      <input
                        type="checkbox"
                        checked={trailer.subtitlesEnabled}
                        onChange={(e) =>
                          updateTrailer(trailer.id, "subtitlesEnabled", e.target.checked)
                        }
                      />
                      <span className={style.mediaToggleSlider} />
                    </label>
                  </div>

                  {trailer.subtitlesEnabled && (
                    <div className={style.mediaAssetsRow}>
                      {[0, 1, 2].map((idx) => (
                        <div key={idx} className={style.assetCellWrapper}>
                          <button
                            type="button"
                            className={style.mediaAssetCell}
                            onClick={() => getSubtitleRef(trailer.id, idx).current.click()}
                          >
                            <input
                              ref={getSubtitleRef(trailer.id, idx)}
                              type="file"
                              accept=".srt"
                              style={{ display: "none" }}
                              onChange={(e) => handleSubtitleFile(trailer.id, idx, e)}
                            />
                            <MdOutlineSubtitles className={style.subtitleFileIcon} />
                            <p className={style.mediaAssetTitle}>
                              {trailer.subtitleFiles[idx]
                                ? subtitleLanguageName(trailer.subtitleFiles[idx].language) ||
                                  trailer.subtitleFiles[idx].name
                                : "Subtitle File"}
                            </p>
                            <p className={style.mediaAssetSub}>
                              {trailer.subtitleFiles[idx]
                                ? trailer.subtitleFiles[idx].isExisting
                                  ? `${subtitleLanguageName(trailer.subtitleFiles[idx].language)} · Saved ✓`
                                  : `${subtitleLanguageName(trailer.subtitleFiles[idx].language)} · Uploaded ✓`
                                : "Upload .srt"}
                            </p>
                          </button>
                          {trailer.subtitleFiles[idx] && (
                            <button
                              type="button"
                              className={style.assetRemoveBtn}
                              onClick={(e) => {
                                e.stopPropagation();
                                removeSubtitleFile(trailer.id, idx);
                              }}
                            >
                              <IoClose />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className={style.trailerSaveBtnRow}>
                    <Button
                      variant="dark"
                      className={style.addTrailerBtn}
                      onClick={() => saveTrailer(trailer.id)}
                    >
                      Save Trailer
                    </Button>
                  </div>

                </div>
              )}

            </div>
          ))}

        </div>

      </Accordion>

      <MediaLibraryModal
        mode="modal"
        isOpen={mediaModalOpen}
        setIsOpen={setMediaModalOpen}
        type={activeAssetType === "thumbnail" ? "image" : "video"}
        showCheckbox={true}
        selectMultipal={false}
        handleVideovalue={activeAssetType === "video" ? handleVideoSelect : () => { }}
        handleimagesvalue={activeAssetType === "thumbnail" ? handleImageSelect : () => { }}
        onSelectVideos={() => { }}
        onSelectImages={() => { }}
        handleUploadImage={() => { }}
        unCheckHandleValue={() => { }}
      />
    </>
  );
};

export default TrailerClips;